/*
 * The ONE place the API base is resolved. Three files used to compute it independently
 * (lib/api.js, contexts/FeatureFlagContext.js, lib/scrcpyStream.js) and each carried its own
 * copy of the same landmine.
 *
 * WHY THIS VALIDATES INSTEAD OF TRUSTING THE ENV VAR
 *
 * 2026-08-25: a dev harness injected a stale absolute REACT_APP_API_BASE (a dead localhost:8081)
 * into the process environment, where it beats every .env file. Login broke for a session. The
 * fix hardcoded '/uiapi' for development only, and left production trusting the variable.
 *
 * 2026-09-08: production got bitten by the same class of bug, one layer down. The build ran from
 * Git Bash as `REACT_APP_API_BASE=/uiapi npm run build`, and MSYS path conversion rewrote the
 * leading-slash value into a WINDOWS PATH before node ever saw it:
 *
 *     REACT_APP_API_BASE=/uiapi   ->   "C:/Program Files/Git/uiapi"
 *
 * That string was baked into the shipped bundle as axios's baseURL. Chrome resolved the
 * drive letter to a file:// URL and refused to load it ("Not allowed to load local resource"),
 * so NO request ever left the browser and the whole app was unreachable behind its login page.
 * Nothing server-side could have helped, and nothing server-side was wrong.
 *
 * The lesson both times: an env var is untrusted input. Validate its SHAPE, do not merely
 * default it when absent. A base is only ever site-relative or an absolute http(s) origin;
 * anything else is a build accident and must not reach a browser.
 *
 * Deliberately dependency-free and side-effect-free so the build-time guard
 * (scripts/check-api-base.js) can require() it directly.
 */

/** The value shipped when the environment cannot be trusted. Same-origin, correct behind nginx. */
export const DEFAULT_API_BASE = '/uiapi';

/**
 * Resolve the API base from a raw env value.
 *
 * @param {string|undefined} rawEnv  process.env.REACT_APP_API_BASE, as given
 * @param {string|undefined} nodeEnv process.env.NODE_ENV
 * @returns {string} a site-relative path or an absolute http(s) origin - never anything else
 */
export function resolveApiBase(rawEnv, nodeEnv) {
  // Development ignores the variable entirely; the dev proxy (src/setupProxy.js) routes
  // the relative path, which also keeps cookies first-party whatever port dev lands on.
  if (nodeEnv === 'development') return DEFAULT_API_BASE;

  const raw = typeof rawEnv === 'string' ? rawEnv.trim() : '';
  if (!raw) return DEFAULT_API_BASE;

  // A Windows drive path ("C:/..." or "C:\..."). This is the 2026-09-08 bug exactly.
  if (/^[A-Za-z]:[\\/]/.test(raw)) return DEFAULT_API_BASE;
  // A UNC path or a protocol-relative URL - neither is a thing we ever mean here.
  if (raw.startsWith('\\\\') || raw.startsWith('//')) return DEFAULT_API_BASE;
  // Any non-http scheme, file: above all.
  if (/^(?!https?:)[A-Za-z][A-Za-z0-9+.-]*:/.test(raw)) return DEFAULT_API_BASE;

  // The two shapes that are actually valid.
  if (raw.startsWith('/')) return raw.replace(/\/+$/, '') || DEFAULT_API_BASE;
  if (/^https?:\/\//i.test(raw)) return raw.replace(/\/+$/, '');

  // A bare word like "uiapi" - almost certainly a missing slash, and it would resolve
  // relative to whatever page the user happens to be on. Refuse it.
  return DEFAULT_API_BASE;
}

/** The resolved base every consumer must use. */
export const API_BASE = resolveApiBase(
  typeof process !== 'undefined' && process.env ? process.env.REACT_APP_API_BASE : undefined,
  typeof process !== 'undefined' && process.env ? process.env.NODE_ENV : undefined
);
